import { all, put, call, takeLatest, select, takeEvery } from 'redux-saga/effects';
import { push } from 'connected-react-router';
import axios from 'axios';
import { message } from 'antd';
import ApiService from '@/services/Api';
import config from '@/services/Api/apiConfig';
import actions, { actionTypes } from './actions';
import leadsActions from '../leads/actions';
import proposalsActions from '../proposals/actions';
import { formatRequestDate } from '@/utils/redux';
import { formatStringToArray } from '@/utils/common';
import { zoom_level } from '../../components/Maps/RegularMap/RegularMap.js';

function* fetchLead({ payload }) {
  try {
    const { data } = yield call(ApiService.fetchLead, payload);
    if (data.project_info == null) {
      message.warning('Lead details are not enriched.', 3);
    }
    yield put(actions.fetchSuccess(data));
  } catch (error) {
    yield put(actions.fetchFailed(error));
  }
}

function* updateLead({ payload }) {
  const { values, emails, phones } = yield select(({ lead }) => ({
    values: lead.editFormData,
    emails: lead.data.customer.emails,
    phones: lead.data.customer.phone_numbers,
  }));

  const requestData = {
    ...values,
    customer: {
      first_name: values.first_name,
      last_name: values.last_name,
      phone_numbers: formatStringToArray(values.phone_numbers, phones),
      emails: formatStringToArray(values.emails, emails),
    },
    property: {
      site_address: values.site_address,
      building_use: values.building_use,
      utility_name: values.utility_name,
      install_type: values.install_type,
      utility_rate_schedule: values.utility_rate_schedule,
      avoided_cost: values.avoided_cost,
      pre_solar_annual_utility_cost: values.pre_solar_annual_utility_cost,
      annual_consumption: values.annual_consumption,
      energy_consumption_estimate: values.energy_consumption_estimate,
      rooftop_solar_area: values.rooftop_solar_area,
      rooftop_solar_capacity: values.rooftop_solar_capacity,
      estimated_epc_cost: values.estimated_epc_cost,
      project_notes: values.project_scope_notes,
    },
    design_data: {
      proposed_system_size: values.proposed_system_size,
      estimated_production: values.estimated_production,
      epc_cost: values.design_data_epc_cost,
      roof_required: values.roof_required,
      roofing_costs: values.roofing_costs,
      survey_status: values.survey_status,
      total_roof_size: values.total_roof_size,
      survey_scheduled_date: formatRequestDate(values.survey_scheduled_date),
      survey_complete_date: formatRequestDate(values.survey_complete_date),
      survey_approved_complete: formatRequestDate(values.survey_approved_complete),
      construction_start_date: formatRequestDate(values.construction_start_date),
      helioscope_link: values.helioscope_link,
      building_use: values.design_data_building_use,
      ess_capacity: values.design_data_ess_capacity,
      ess_epc_price: values.design_data_ess_epc_price,
      ess_warranty_length: values.ess_warranty_length,
      roof_warranty_length: values.roof_warranty_length,
    },
    project_info: {
      system_size: values.project_info_system_size,
      estimated_year_production: values.project_info_estimated_year_production,
      epc_price: values.project_info_epc_price,
      avoided_cost: values.project_info_avoided_cost,
      install_type: values.project_info_install_type,
      ess_capacity: values.project_info_ess_capacity,
      ess_size: values.project_info_ess_size,
      ess_epc_price: values.project_info_ess_epc_price,
      ess_warranty_length: values.project_info_ess_warranty_length,
    },
    energy_reseller: values.energy_reseller,
    owner_type: values.owner_type,
    zoom_level: zoom_level,
  };
  try {
    const { data } = yield call(ApiService.updateLead, payload.leadId, requestData);
    if (data.project_info.estimated_year_production == null || data.project_info.ess_epc_price == null) {
      const { data: project_information_data } = yield call(ApiService.fetchProjectInformationDetails, payload.leadId);
      data.project_info.estimated_year_production = project_information_data.estimated_year_production;
      data.project_info.ess_epc_price = project_information_data.ess_epc_price;
    }
    yield put(actions.updateSuccess(data));
    yield put(proposalsActions.clearCreateErrors());
  } catch (error) {
    yield put(actions.updateFailed(error));
  }
}

function* deleteLead({ payload }) {
  try {
    yield call(ApiService.deleteLead, payload.id);
    yield put(actions.deleteSuccess());

    const { deleteSucceed, tableOptions } = yield select(({ lead, leads }) => ({
      deleteSucceed: lead.deleteSucceed,
      tableOptions: leads.tableOptions,
    }));

    if (deleteSucceed) {
      if (payload.location === 'lead') {
        yield put(push('/manage/leads'));
      } else {
        yield put(leadsActions.fetchLeadsSummary());
        yield put(
          leadsActions.fetchLeads({
            search: tableOptions.search,
            page: tableOptions.page,
            limit: tableOptions.limit,
            ordering:
              tableOptions.sorter.order && tableOptions.sorter.field
                ? `${tableOptions.sorter.order}__${tableOptions.sorter.field}`
                : null,
            ...tableOptions.filters,
          })
        );
      }
    }
  } catch (error) {
    yield put(actions.deleteFailed(error));
  }
}

function* deleteFile({ payload }) {
  let url;
  if (payload.entity === 'utility_bills') {
    url = '/delete_utility_bill';
  } else if (payload.entity === 'additional_files') {
    url = '/delete_additional_file';
  }
  try {
    yield call(ApiService.deleteLeadFile, url, payload.fileId);
    message.success('File deleted successfully.');
    yield put(actions.fetchLead(payload.leadId));
  } catch (error) {
    message.error('Some error has occurred. Please try again.');
  }
}

function* saveEditData({ payload }) {
  yield put(actions.saveEditData(payload));
}

function* fillLeadFields({ payload }) {
  const { editFormData } = yield select(({ lead }) => ({
    editFormData: lead.editFormData,
  }));
  const fieldsValueObject = {};
  const dataSourceItemObject = {
    ...payload.form.getFieldsValue(),
    ...editFormData,
  };
  Object.keys(payload.form.getFieldsValue()).forEach((key) => {
    fieldsValueObject[key] = dataSourceItemObject[key];
  });
  payload.form.setFieldsValue(fieldsValueObject);
}

function* redirectToLead({ payload }) {
  yield put(push(`/manage/leads/${payload}`));
}

function* uploadSystemImage({ payload }) {
  const { file, onSuccess, token, leadId } = payload;
  const formData = new FormData();
  formData.append('system_image', file);

  try {
    const { data } = yield call(axios, {
      method: 'patch',
      url: `${config.BASE_URI}/api/update_lead/${leadId}/`,
      data: formData,
      headers: {
        'Content-Type': `multipart/form-data; boundary=${formData._boundary}`,
        Authorization: `Token ${token}`,
      },
    });

    yield call(onSuccess, data);
    yield put(actions.updateSuccess(data));
  } catch (error) {
    yield put(actions.updateFailed(error));
  }
}

function* removeSystemImage({ payload }) {
  try {
    const { data } = yield call(ApiService.updateLead, payload, { system_image: null });
    yield put(actions.updateSuccess(data));
  } catch (error) {
    yield put(actions.updateFailed(error));
  }
}

function* fetchDocument({ payload }) {
  try {
    const { data } = yield call(ApiService.fetchDocument, payload);
    yield put(actions.fetchDocumentSuccess(data));
  } catch (error) {
    yield put(actions.fetchDocumentFailed(error));
  }
}

function* resetDocumentUrl() {
  try {
    //const { data } = {};
    yield put(actions.resetDocumentUrlSuccess());
  } catch (error) {
    yield put(actions.resetDocumentUrlFailed(error));
  }
}

export default function* () {
  yield all([
    takeLatest(actionTypes.FETCH_LEAD, fetchLead),
    takeLatest(actionTypes.UPDATE_LEAD, updateLead),
    takeLatest(actionTypes.DELETE_FILE, deleteFile),
    takeLatest(actionTypes.DELETE_LEAD, deleteLead),
    takeLatest(actionTypes.FILL_LEAD_FIELDS, fillLeadFields),
    takeEvery('SAVE_EDIT_DATA', saveEditData),
    takeEvery(actionTypes.REDIRECT_TO_LEAD, redirectToLead),
    takeLatest(actionTypes.SYSTEM_IMAGE_UPLOAD, uploadSystemImage),
    takeLatest(actionTypes.REMOVE_SYSTEM_IMAGE, removeSystemImage),
    takeLatest(actionTypes.FETCH_DOCUMENT, fetchDocument),
    takeLatest(actionTypes.RESET_DOCUMENT_URL, resetDocumentUrl),
  ]);
}
